import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Grid, Button, TextField, FormControl, InputLabel, Select,
  MenuItem, IconButton, Tooltip, Chip, Checkbox, Dialog, DialogTitle,
  DialogContent, DialogActions, Link
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { fetchData, postData, putData, deleteData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import Toast from "../../DynamicComponent/Toast";
import ConfirmDialog from "../../DynamicComponent/ConfirmDialog";

// ─── Constants ───────────────────────────────────────────────────────────────
const GRADES = [
  "All",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8",
  "MunMazhalai", "Mazhalai",
];

const SECTIONS = ["All", "Sec A", "Sec B", "Sec C"];

const SUBJECTS = [
  "All",
  "Tamil Reading",
  "Tamil Writing",
  "Tamil Grammar",
  "Tamil Speaking",
];

const ACADEMIC_YEARS = ["All", "2024-2025", "2025-2026", "2026-2027"];

const FALLBACK_ROWS = [
  {
    id: 1, grade: "Nilai 1", section: "Sec A", subject: "Tamil Reading",
    teacherName: "Mrs. Meena Subramaniam",
    classroomUrl: "https://classroom.google.com/c/NjA1MzI2",
    classroomCode: "abc123", academicYear: "2025-2026", status: "Active",
  },
  {
    id: 2, grade: "Nilai 3", section: "Sec B", subject: "Tamil Writing",
    teacherName: "Mr. Rajan Pillai",
    classroomUrl: "https://classroom.google.com/c/NzA2NDM3",
    classroomCode: "def456", academicYear: "2025-2026", status: "Active",
  },
  {
    id: 3, grade: "Nilai 5", section: "Sec A", subject: "Tamil Grammar",
    teacherName: "Mrs. Kavitha Nair",
    classroomUrl: "https://classroom.google.com/c/ODA3NTQ4",
    classroomCode: "ghi789", academicYear: "2025-2026", status: "Active",
  },
  {
    id: 4, grade: "MunMazhalai", section: "Sec C", subject: "Tamil Speaking",
    teacherName: "Ms. Priya Krishnan",
    classroomUrl: "https://classroom.google.com/c/OTAyMzQ1",
    classroomCode: "jkl012", academicYear: "2025-2026", status: "Active",
  },
  {
    id: 5, grade: "Nilai 7", section: "Sec B", subject: "Tamil Reading",
    teacherName: "Mr. Anand Venkat",
    classroomUrl: "https://classroom.google.com/c/MTAxMjM0",
    classroomCode: "mno345", academicYear: "2025-2026", status: "Active",
  },
];

const EMPTY_FORM = {
  grade: "",
  section: "",
  subject: "",
  teacherName: "",
  classroomUrl: "",
  classroomCode: "",
  academicYear: "",
  status: "Active",
};

// ─── URL Validator ────────────────────────────────────────────────────────────
const isValidClassroomUrl = (url) =>
  typeof url === "string" && url.startsWith("https://classroom.google.com/");

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminGoogleClassroom = () => {
  const [classrooms, setClassrooms] = useState([]);
  const [filters, setFilters] = useState({
    grade: "All", section: "All", subject: "All",
    academicYear: "All", search: "",
  });
  const [selectedIds, setSelectedIds] = useState([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Toast
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadClassrooms = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.grade !== "All") params.append("grade", filters.grade);
    if (filters.section !== "All") params.append("section", filters.section);
    if (filters.subject !== "All") params.append("subject", filters.subject);
    if (filters.academicYear !== "All") params.append("year", filters.academicYear);
    if (filters.search) params.append("search", filters.search);

    try {
      const result = await fetchData(`admin/google-classroom?${params.toString()}`);
      const data = result?.data || result;
      if (Array.isArray(data) && data.length > 0) {
        setClassrooms(data);
      } else {
        setClassrooms(FALLBACK_ROWS);
      }
    } catch {
      setClassrooms(FALLBACK_ROWS);
    }
  }, [filters]);

  useEffect(() => { loadClassrooms(); }, [loadClassrooms]);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ── Checkbox helpers ──────────────────────────────────────────────────────
  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? classrooms.map((c) => c.id) : []);
  };

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };

  // ── Status Toggle ─────────────────────────────────────────────────────────
  const handleToggleStatus = async (item) => {
    const newStatus = item.status === "Active" ? "Inactive" : "Active";
    try {
      await putData(`admin/google-classroom/${item.id}`, { status: newStatus });
      setToast({ open: true, severity: "success", message: `Status updated to ${newStatus}` });
      loadClassrooms();
    } catch {
      // Optimistic UI fallback
      setClassrooms((prev) =>
        prev.map((c) => c.id === item.id ? { ...c, status: newStatus } : c)
      );
      setToast({ open: true, severity: "success", message: `Status updated to ${newStatus}` });
    }
  };

  // ── Add / Edit Dialog ─────────────────────────────────────────────────────
  const openAdd = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      grade: item.grade || "",
      section: item.section || "",
      subject: item.subject || "",
      teacherName: item.teacherName || "",
      classroomUrl: item.classroomUrl || "",
      classroomCode: item.classroomCode || "",
      academicYear: item.academicYear || "",
      status: item.status || "Active",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.grade) errors.grade = "Grade is required";
    if (!form.section) errors.section = "Section is required";
    if (!form.subject) errors.subject = "Subject is required";
    if (!form.classroomUrl) {
      errors.classroomUrl = "URL is required";
    } else if (!isValidClassroomUrl(form.classroomUrl)) {
      errors.classroomUrl = "Must start with https://classroom.google.com/";
    }
    if (!form.classroomCode) errors.classroomCode = "Classroom code is required";
    if (!form.academicYear) errors.academicYear = "Academic year is required";
    return errors;
  };

  const handleDialogSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      if (editingItem) {
        await putData(`admin/google-classroom/${editingItem.id}`, form);
        setToast({ open: true, severity: "success", message: "Classroom link updated" });
      } else {
        await postData("admin/google-classroom", form);
        setToast({ open: true, severity: "success", message: "Classroom link added" });
      }
      setDialogOpen(false);
      loadClassrooms();
    } catch {
      // Local fallback
      if (editingItem) {
        setClassrooms((prev) =>
          prev.map((c) => c.id === editingItem.id ? { ...c, ...form } : c)
        );
        setToast({ open: true, severity: "success", message: "Classroom link updated" });
      } else {
        setClassrooms((prev) => [
          ...prev,
          { ...form, id: Date.now() },
        ]);
        setToast({ open: true, severity: "success", message: "Classroom link added" });
      }
      setDialogOpen(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteData(`admin/google-classroom/${deleteTarget.id}`);
      setToast({ open: true, severity: "success", message: "Classroom link deleted" });
    } catch {
      setClassrooms((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setToast({ open: true, severity: "success", message: "Classroom link deleted" });
    }
    setConfirmOpen(false);
    setDeleteTarget(null);
    loadClassrooms();
  };

  // ── Export CSV ────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = ["Grade", "Section", "Subject", "Teacher Name", "Classroom URL", "Classroom Code", "Academic Year", "Status"];
    const rows = classrooms.map((c) => [
      c.grade, c.section, c.subject, c.teacherName,
      c.classroomUrl, c.classroomCode, c.academicYear, c.status,
    ]);
    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${v || ""}"`).join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "google_classrooms.csv";
    a.click();
    URL.revokeObjectURL(url);
    setToast({ open: true, severity: "info", message: "CSV exported" });
  };

  // ── Build table rows ──────────────────────────────────────────────────────
  const allSelected = classrooms.length > 0 && selectedIds.length === classrooms.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < classrooms.length;

  const columns = [
    { label: (
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => toggleSelectAll(e.target.checked)}
          sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" } }}
        />
      ), field: "checkbox" },
    { label: "Grade (NILAI)", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Subject", field: "subject" },
    { label: "Teacher Name", field: "teacherName" },
    { label: "Google Classroom URL", field: "classroomUrlCell" },
    { label: "Classroom Code", field: "classroomCode" },
    { label: "Academic Year", field: "academicYear" },
    { label: "Status", field: "statusChip" },
    { label: "Actions", field: "actions" },
  ];

  const tableRows = classrooms.map((item) => ({
    ...item,
    checkbox: (
      <Checkbox
        size="small"
        checked={selectedIds.includes(item.id)}
        onChange={(e) => toggleSelectOne(item.id, e.target.checked)}
      />
    ),
    classroomUrlCell: (
      <Link
        href={item.classroomUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#244C77", fontSize: 13 }}
      >
        Open Link <OpenInNewIcon sx={{ fontSize: 14 }} />
      </Link>
    ),
    statusChip: (
      <Chip
        label={item.status}
        size="small"
        sx={{
          backgroundColor: item.status === "Active" ? "#e8f5e9" : "#fce4ec",
          color: item.status === "Active" ? "#2e7d32" : "#c62828",
          fontWeight: 600,
          fontSize: 12,
        }}
      />
    ),
    actions: (
      <Box sx={{ display: "flex", gap: 0.5 }}>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => openEdit(item)}>
            <EditIcon fontSize="small" sx={{ color: "#244C77" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={item.status === "Active" ? "Deactivate" : "Activate"}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleToggleStatus(item)}
            sx={{
              textTransform: "none",
              fontSize: 11,
              px: 1,
              borderColor: item.status === "Active" ? "#f59e0b" : "#4caf50",
              color: item.status === "Active" ? "#f59e0b" : "#4caf50",
              "&:hover": {
                borderColor: item.status === "Active" ? "#d97706" : "#2e7d32",
                backgroundColor: item.status === "Active" ? "#fffbeb" : "#e8f5e9",
              },
            }}
          >
            {item.status === "Active" ? "Deactivate" : "Activate"}
          </Button>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={() => handleDeleteClick(item)}>
            <DeleteIcon fontSize="small" sx={{ color: "#ef4444" }} />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  }));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb + Title */}
      <Typography variant="body2" color="text.secondary" mb={1}>
        Pages / Google Classroom Management
      </Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Google Classroom Management
      </Typography>

      {/* Filter Bar */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: "#F9FAFB",
          borderRadius: 2,
          border: "1px solid #E5E7EB",
        }}
      >
        {/* Grade */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Grade</InputLabel>
          <Select
            value={filters.grade}
            label="Grade"
            onChange={(e) => handleFilterChange("grade", e.target.value)}
          >
            {GRADES.map((g) => (
              <MenuItem key={g} value={g}>{g}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Section */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Section</InputLabel>
          <Select
            value={filters.section}
            label="Section"
            onChange={(e) => handleFilterChange("section", e.target.value)}
          >
            {SECTIONS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Subject */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            value={filters.subject}
            label="Subject"
            onChange={(e) => handleFilterChange("subject", e.target.value)}
          >
            {SUBJECTS.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Academic Year */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Academic Year</InputLabel>
          <Select
            value={filters.academicYear}
            label="Academic Year"
            onChange={(e) => handleFilterChange("academicYear", e.target.value)}
          >
            {ACADEMIC_YEARS.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search teacher, code..."
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && loadClassrooms()}
          sx={{ minWidth: 220 }}
        />

        {/* Spacer + Add Button */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            sx={{ textTransform: "none" }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{
              background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
              textTransform: "none",
              "&:hover": { background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)" },
            }}
          >
            Add Classroom Link
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <DynamicTable columns={columns} rows={tableRows} />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            {editingItem ? "Edit Classroom Link" : "Add Classroom Link"}
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <Grid container spacing={2}>
            {/* Grade */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.grade}>
                <InputLabel>Grade *</InputLabel>
                <Select
                  value={form.grade}
                  label="Grade *"
                  onChange={(e) => setForm((p) => ({ ...p, grade: e.target.value }))}
                >
                  {GRADES.filter((g) => g !== "All").map((g) => (
                    <MenuItem key={g} value={g}>{g}</MenuItem>
                  ))}
                </Select>
                {formErrors.grade && (
                  <Typography variant="caption" color="error">{formErrors.grade}</Typography>
                )}
              </FormControl>
            </Grid>

            {/* Section */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.section}>
                <InputLabel>Section *</InputLabel>
                <Select
                  value={form.section}
                  label="Section *"
                  onChange={(e) => setForm((p) => ({ ...p, section: e.target.value }))}
                >
                  {SECTIONS.filter((s) => s !== "All").map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
                {formErrors.section && (
                  <Typography variant="caption" color="error">{formErrors.section}</Typography>
                )}
              </FormControl>
            </Grid>

            {/* Subject */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Subject *"
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                error={!!formErrors.subject}
                helperText={formErrors.subject}
              />
            </Grid>

            {/* Teacher Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Teacher Name"
                value={form.teacherName}
                onChange={(e) => setForm((p) => ({ ...p, teacherName: e.target.value }))}
                helperText="Optional — admin may reassign"
              />
            </Grid>

            {/* Google Classroom URL */}
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Google Classroom URL *"
                placeholder="https://classroom.google.com/c/..."
                value={form.classroomUrl}
                onChange={(e) => setForm((p) => ({ ...p, classroomUrl: e.target.value }))}
                error={!!formErrors.classroomUrl}
                helperText={formErrors.classroomUrl || "Must start with https://classroom.google.com/"}
              />
            </Grid>

            {/* Classroom Code */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Classroom Code *"
                value={form.classroomCode}
                onChange={(e) => setForm((p) => ({ ...p, classroomCode: e.target.value }))}
                error={!!formErrors.classroomCode}
                helperText={formErrors.classroomCode}
              />
            </Grid>

            {/* Academic Year */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Academic Year *"
                placeholder="e.g. 2025-2026"
                value={form.academicYear}
                onChange={(e) => setForm((p) => ({ ...p, academicYear: e.target.value }))}
                error={!!formErrors.academicYear}
                helperText={formErrors.academicYear}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  label="Status"
                  onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDialogSave}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: 2,
              background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
              "&:hover": { background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)" },
            }}
          >
            {editingItem ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Classroom Link"
        message={`Are you sure you want to delete the classroom link for "${deleteTarget?.grade} – ${deleteTarget?.section}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setConfirmOpen(false); setDeleteTarget(null); }}
      />

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      <Toast
        open={toast.open}
        handleClose={() => setToast((t) => ({ ...t, open: false }))}
        severity={toast.severity}
        message={toast.message}
      />
    </Box>
  );
};

export default AdminGoogleClassroom;
